import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'

import { flushQueue } from '@/features/work-sessions/sync'
import { useSession } from '@/lib/auth'
import { livePendingEvents } from '@/lib/db'

// Retry co 15 s dopóki cokolwiek czeka w kolejce. `online`/`visibilitychange`
// łapią typowe przypadki, ale nie wszystkie: iOS potrafi wrócić z tła bez
// zdarzenia `online`, a Wi-Fi kliniki bywa „podłączone, ale bez internetu"
// (navigator.onLine === true, request pada) — wtedy jedynym wyjściem jest tick.
// Krócej niż kiedyś, bo zaległy zapis blokuje teraz przycisk start/koniec.
const RETRY_INTERVAL_MS = 15_000

/**
 * Opróżnia zaległą kolejkę offline (zapisy poprzedniej wersji aplikacji).
 * Wysyła wyłącznie przy zalogowanym użytkowniku — bez sesji każda próba to
 * 401, a zdarzenie i tak musi poczekać na logowanie.
 */
export default function SyncController() {
  const qc = useQueryClient()
  const session = useSession()
  const pendingCount = useLiveQuery(() => livePendingEvents().count(), [], 0)
  const hasPending = pendingCount > 0

  // `undefined` = jeszcze nie wiemy, `null` = wylogowany, string = zalogowany.
  const userId = session.data === undefined ? undefined : (session.data?.id ?? null)
  const loggedIn = typeof userId === 'string'
  const loggedInRef = useRef(loggedIn)
  loggedInRef.current = loggedIn
  const prevUserIdRef = useRef<string | null | undefined>(undefined)

  // Brakujące ogniwo z incydentu: po zalogowaniu nic nie wysyłało kolejki,
  // więc „Koniec pracy" wyprzedzał zaległy „start" i dostawał 409. Przejście
  // z „wylogowany" na „zalogowany" wysyła od razu, bez czekania na backoff.
  // Pierwsze ustalenie sesji po starcie aplikacji to zwykły flush przy montowaniu.
  useEffect(() => {
    const prev = prevUserIdRef.current
    prevUserIdRef.current = userId
    if (typeof userId !== 'string' || prev === userId) return
    if (navigator.onLine) {
      void flushQueue(qc, { ignoreBackoff: prev === null })
    }
  }, [userId, qc])

  useEffect(() => {
    const tryFlush = () => {
      if (loggedInRef.current && navigator.onLine) void flushQueue(qc)
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tryFlush()
    }
    window.addEventListener('online', tryFlush)
    // `pageshow` — powrót z bfcache nie odpala ani `online`, ani `visibilitychange`.
    window.addEventListener('pageshow', tryFlush)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('online', tryFlush)
      window.removeEventListener('pageshow', tryFlush)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [qc])

  useEffect(() => {
    if (!hasPending || !loggedIn) return
    const handle = setInterval(() => {
      if (navigator.onLine) void flushQueue(qc)
    }, RETRY_INTERVAL_MS)
    return () => clearInterval(handle)
  }, [hasPending, loggedIn, qc])

  return null
}
